// useAoiStore.ts

import { ref, readonly } from 'vue';
import { useFilterStore } from './useFilterStore';
import { normalizeUploadedGeoJson, normalizeUploadedGeoJsonAsync } from '@/uploadedGeometryValidation';
import { authFetch } from './useAuthFetch';
import { cacheKey, hashAoi, readCache, writeCache } from './useAoiWarmCache';

/* ──────────────────────────── Types ──────────────────────────── */

export interface AoiCoverageResult {
  /** Raw GeoJSON response from the coverage API */
  geojson: Record<string, unknown>;
  /** ISO timestamp of when the query was made */
  queriedAt: string;
  /** Human-readable summary */
  summary: string;
}

export interface LoadedAoiFile {
  file: File;
  geojson: Record<string, unknown>;
}

export type AoiStatus = 'idle' | 'validating' | 'uploading' | 'success' | 'error';

/* ── Satellite name → NORAD ID mapping ── */
export const SAT_NORAD_MAP: Record<string, number> = {
  'FOREST-4': 63357,
  'FOREST-5': 63354,
  'FOREST-6': 63353,
  'FOREST-7': 63358,
  'FOREST-8': 63355,
  'FOREST-9': 63352,
  'FOREST-10': 63356,
  'FOREST-11': 63351,
  'FOREST-12': 67379,
  'FOREST-13': 67377,
  'FOREST-14': 67381,
  'FOREST-15': 67378,
  'FOREST-16': 68998,
  'FOREST-17': 68988,
  'FOREST-18': 68999,
  'FOREST-19': 69001,
  'FOREST-2': 56970,
  'FOREST-3': 62682,
  'GENA-OT': 66774,
  'AQUA': 27424,
  'TERRA': 25994,
  'NOAA 20 (JPSS-1)': 43013,
  'NOAA 21 (JPSS-2)': 54234,
  'SUOMI NPP': 37849,
  'SENTINEL-2A': 40697,
  'SENTINEL-2B': 42063,
  'SENTINEL-2C': 60989,
  'SENTINEL-3A': 41335,
  'SENTINEL-3B': 43437,
  'LANDSAT 8': 39084,
  'LANDSAT 9': 49260,
  'FENGYUN 3D': 43010,
  'FENGYUN 3E': 49008,
  'FENGYUN 3F': 57490,
  'GCOM-C': 43065,
  'METOP-B': 38771,
  'METOP-C': 43689,
  'SENTINEL-5P': 42969,
  // GEO (disabled for now – uncomment to re-enable)
  // 'GOES 18': 51850,
  // 'GOES 19': 60133,
  // 'HIMAWARI-9': 41836,
  // 'GEO-KOMPSAT-2A': 43823,
  // 'METEOSAT-9 (MSG-2)': 28912,
  // 'METEOSAT-10 (MSG-3)': 38552,
  // 'METEOSAT-11 (MSG-4)': 40732,
  // 'METEOSAT-12 (MTG-I1)': 54743,
};

/* ── Reverse map: NORAD ID → display name ── */
const NORAD_TO_NAME: Record<number, string> = {};
for (const [name, norad] of Object.entries(SAT_NORAD_MAP)) {
  NORAD_TO_NAME[norad] = name;
}

/* ── Satellite metadata catalog (fallback when coverage API omits fields) ── */
export interface SatMetadata {
  sensor_type: string;
  spatial_res_m: number;
  constellation: string;
}

export const SAT_METADATA: Record<string, SatMetadata> = {
  'FOREST-4':  { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-5':  { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-6':  { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-7':  { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-8':  { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-9':  { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-10': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-11': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-12': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-13': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-14': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-15': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-16': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-17': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-18': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-19': { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-2':  { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'FOREST-3':  { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'GENA-OT':   { sensor_type: 'SAFIRE', spatial_res_m: 200, constellation: 'OroraTech Constellation' },
  'AQUA':               { sensor_type: 'MODIS',   spatial_res_m: 1000, constellation: 'EOS' },
  'TERRA':              { sensor_type: 'MODIS',   spatial_res_m: 1000, constellation: 'EOS' },
  'NOAA 20 (JPSS-1)':   { sensor_type: 'VIIRS',   spatial_res_m: 300,  constellation: 'JPSS' },
  'NOAA 21 (JPSS-2)':   { sensor_type: 'VIIRS',   spatial_res_m: 300,  constellation: 'JPSS' },
  'SUOMI NPP':          { sensor_type: 'VIIRS',   spatial_res_m: 300,  constellation: 'JPSS' },
  'SENTINEL-2A':        { sensor_type: 'MSI',     spatial_res_m: 10,   constellation: 'Sentinel-2' },
  'SENTINEL-2B':        { sensor_type: 'MSI',     spatial_res_m: 10,   constellation: 'Sentinel-2' },
  'SENTINEL-2C':        { sensor_type: 'MSI',     spatial_res_m: 10,   constellation: 'Sentinel-2' },
  'SENTINEL-3A':        { sensor_type: 'SLSTR',   spatial_res_m: 500,  constellation: 'Sentinel-3' },
  'SENTINEL-3B':        { sensor_type: 'SLSTR',   spatial_res_m: 500,  constellation: 'Sentinel-3' },
  'LANDSAT 8':          { sensor_type: 'OLI',     spatial_res_m: 30,   constellation: 'Landsat' },
  'LANDSAT 9':          { sensor_type: 'OLI-2',   spatial_res_m: 30,   constellation: 'Landsat' },
  'FENGYUN 3D':         { sensor_type: 'MERSI-2', spatial_res_m: 250,  constellation: 'FengYun-3' },
  'FENGYUN 3E':         { sensor_type: 'MERSI-LL', spatial_res_m: 250, constellation: 'FengYun-3' },
  'FENGYUN 3F':         { sensor_type: 'MERSI-3', spatial_res_m: 250,  constellation: 'FengYun-3' },
  'GCOM-C':             { sensor_type: 'SGLI',    spatial_res_m: 250,  constellation: 'GCOM' },
  'METOP-B':            { sensor_type: 'AVHRR',   spatial_res_m: 1000, constellation: 'MetOp' },
  'METOP-C':            { sensor_type: 'AVHRR',   spatial_res_m: 1000, constellation: 'MetOp' },
  'SENTINEL-5P':        { sensor_type: 'TROPOMI', spatial_res_m: 5500, constellation: 'Sentinel-5P' },
  // GEO (disabled for now – uncomment to re-enable)
  // 'GOES 18':            { sensor_type: 'ABI',     spatial_res_m: 2000, constellation: 'GOES' },
  // 'GOES 19':            { sensor_type: 'ABI',     spatial_res_m: 2000, constellation: 'GOES' },
  // 'HIMAWARI-9':         { sensor_type: 'AHI',     spatial_res_m: 2000, constellation: 'Himawari' },
  // 'GEO-KOMPSAT-2A':     { sensor_type: 'AMI',     spatial_res_m: 2000, constellation: 'GEO-KOMPSAT' },
  // 'METEOSAT-9 (MSG-2)': { sensor_type: 'SEVIRI',  spatial_res_m: 3000, constellation: 'Meteosat' },
  // 'METEOSAT-10 (MSG-3)': { sensor_type: 'SEVIRI', spatial_res_m: 3000, constellation: 'Meteosat' },
  // 'METEOSAT-11 (MSG-4)': { sensor_type: 'SEVIRI', spatial_res_m: 3000, constellation: 'Meteosat' },
  // 'METEOSAT-12 (MTG-I1)': { sensor_type: 'FCI',   spatial_res_m: 1000, constellation: 'Meteosat' },
};

/**
 * Look up satellite metadata by name (case-insensitive).
 */
export function getSatMetadata(name: string): SatMetadata | undefined {
  // Direct match first
  if (SAT_METADATA[name]) return SAT_METADATA[name];
  // Case-insensitive fallback
  const upper = name.toUpperCase();
  for (const [key, meta] of Object.entries(SAT_METADATA)) {
    if (key.toUpperCase() === upper) return meta;
  }
  return undefined;
}

/**
 * Resolve a satellite display name from a feature's properties.
 *
 * IMPORTANT:
 * Prefer NORAD ID resolution when possible, so external catalog names like
 * "LEMUR-2-..." don't override our internal display names like "FOREST-4".
 */
export function resolveSatelliteName(props: Record<string, unknown>): string {
  // 1) Prefer NORAD ID → display name lookup (if available)
  const noradId = props.norad_id ?? props.norad_cat_id ?? props.NORAD_ID;
  if (noradId != null) {
    const resolved = NORAD_TO_NAME[Number(noradId)];
    if (resolved) return resolved;
  }

  // 2) Fall back to direct display name fields from API/GeoJSON
  const directName = props.satellite ?? props.name ?? props.sat_name ?? props.satellite_name;
  if (directName) return String(directName);

  // 3) Last resort
  if (noradId != null) return `NORAD-${noradId}`;
  return '';
}

/* ──────────────────────── Shared State ─────────────────────── */

const AOI_MAX_BYTES = 5 * 1024 * 1024; // 5 MiB

const aoiFile = ref<File | null>(null);
const aoiGeoJson = ref<Record<string, unknown> | null>(null);
const coverageResult = ref<AoiCoverageResult | null>(null);
const status = ref<AoiStatus>('idle');
const errorMessage = ref<string | null>(null);
const noticeMessage = ref<string | null>(null);

/* ──────────────────────── Helpers ──────────────────────────── */

function normalizeGeoJson(data: unknown) {
  return normalizeUploadedGeoJson(data);
}

/**
 * Bring a coverage response onto the property contract the AOI views read.
 *
 * This mirrors `_normalize_pass_features` in function/main.py and cannot simply be
 * deleted in favour of it: in dev, vite proxies `/api/coverage` straight to the
 * predictor's `/passes` (see vite.config.js), so the backend never runs and this is
 * the only normalization there is.
 */
function normalizeCoverageDocument(document: Record<string, unknown>): Record<string, unknown> {
  const features = Array.isArray(document.features)
    ? document.features.map((rawFeature) => {
      if (!rawFeature || typeof rawFeature !== 'object') return rawFeature;
      const feature = rawFeature as Record<string, unknown>;
      const rawProperties = feature.properties;
      const properties = rawProperties && typeof rawProperties === 'object'
        ? rawProperties as Record<string, unknown>
        : {};
      const cloudCoverage = properties.cloud_cover_pct ?? properties.cloud_coverage ?? null;
      const dataSource = String(properties.data_source ?? 'api');
      const isScheduled = dataSource === 'forest_client';
      const starboard = Number(properties.swath_starboard_km);
      const port = Number(properties.swath_port_km);
      const hasSwathSides = properties.swath_starboard_km != null
        && properties.swath_port_km != null
        && Number.isFinite(starboard)
        && Number.isFinite(port);

      return {
        ...feature,
        properties: {
          ...properties,
          type: properties.type ?? 'coverage',
          data_source: dataSource,
          // Anything from /passes is already trimmed to actual AOI contact, so
          // clipTimesToAoi must leave its times alone. Scheduled footprints carry
          // their whole tasking window and do still need clipping — a blanket
          // `?? true` here silently turned that off for them and made every
          // scheduled pass look far longer than its real contact.
          // Keyed on data_source rather than on the backend's flag alone, because
          // the dev proxy above means the flag may not be set at all.
          times_exact: properties.times_exact ?? !isScheduled,
          cloud_cover_pct: cloudCoverage,
          ...(cloudCoverage != null && !properties.cloud_source ? { cloud_source: 'api' } : {}),
          ...(properties.swath_width_km == null && hasSwathSides
            ? { swath_width_km: starboard + port }
            : {}),
        },
      };
    })
    : [];

  return { ...document, type: 'FeatureCollection', features };
}

async function normalizeGeoJsonAsync(data: unknown) {
  return normalizeUploadedGeoJsonAsync(data, {
    onRepairStart: async (message) => {
      noticeMessage.value = message;
      if (typeof window === 'undefined') return;
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    },
  });
}

/* ──────────────────────── Actions ──────────────────────────── */

async function loadAoiFile(file: File): Promise<LoadedAoiFile | null> {
  status.value = 'validating';
  aoiFile.value = null;
  aoiGeoJson.value = null;
  errorMessage.value = null;
  noticeMessage.value = null;
  coverageResult.value = null;

  if (file.size > AOI_MAX_BYTES) {
    errorMessage.value = `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 5 MB.`;
    status.value = 'error';
    return null;
  }

  let text: string;
  try {
    text = await file.text();
  } catch {
    errorMessage.value = 'Failed to read file';
    status.value = 'error';
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    errorMessage.value = 'File is not valid JSON';
    status.value = 'error';
    return null;
  }

  const normalized = await normalizeGeoJsonAsync(parsed);
  const normalizedGeoJson = normalized.geojson;
  if (normalized.error || !normalizedGeoJson) {
    errorMessage.value = normalized.error ?? 'Invalid GeoJSON';
    noticeMessage.value = null;
    status.value = 'error';
    return null;
  }

  aoiFile.value = file;
  aoiGeoJson.value = normalizedGeoJson;
  status.value = 'idle';
  noticeMessage.value = normalized.notes.length > 0 ? normalized.notes.join(' ') : null;
  return { file, geojson: normalizedGeoJson };
}

async function submitCoverage(
  options: {
    includeGeometry?: boolean;
    geojson?: Record<string, unknown>;
    quiet?: boolean;
    skipCache?: boolean;
    range?: readonly [number, number];
    signal?: AbortSignal;
  } = {},
): Promise<AoiCoverageResult | null> {
  // `geojson`/`quiet` let the warmer drive this for an area that is not the one
  // currently selected, without disturbing the visible status or result.
  const targetGeoJson = options.geojson ?? aoiGeoJson.value;
  const includeGeometry = options.includeGeometry !== false;
  const quiet = options.quiet === true;
  if (!targetGeoJson) {
    errorMessage.value = 'No AOI loaded';
    status.value = 'error';
    return null;
  }

  const { metadata, timeRange, loadMetadata, servedCatalogNoradIds } = useFilterStore();
  await loadMetadata();
  if (!metadata.value) {
    errorMessage.value = 'Forecast metadata is unavailable';
    status.value = 'error';
    return null;
  }

  // Coverage computation must always include the full configured catalog,
  // regardless of the current UI satellite filter selection — but the catalog the
  // service serves, not this file's copy of it.  Sending an ID the service does not
  // serve fails the whole query rather than returning a shorter result set.
  const noradIds = servedCatalogNoradIds();
  if (noradIds.length === 0) {
    errorMessage.value = 'No NORAD IDs configured';
    status.value = 'error';
    return null;
  }

  // Upstream coverage API is strict about ISO-8601 formatting; send whole-second UTC timestamps.
  const toApiIso = (epochMs: number): string =>
    new Date(epochMs).toISOString().replace(/\.\d{3}Z$/, 'Z');
  if (!quiet) {
    status.value = 'uploading';
    errorMessage.value = null;
  }

  const body = JSON.stringify(targetGeoJson);

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let abortFromCaller: (() => void) | null = null;
  try {
    const controller = new AbortController();
    abortFromCaller = () => controller.abort();
    if (options.signal?.aborted) {
      controller.abort();
    } else {
      options.signal?.addEventListener('abort', abortFromCaller, { once: true });
    }
    timeoutId = setTimeout(() => controller.abort(), 120_000);
    const selectedRange = options.range ?? timeRange.value;
    const rangeStart = selectedRange[0];
    const rangeEnd = selectedRange[1];
    if (!Number.isFinite(rangeStart) || !Number.isFinite(rangeEnd) || rangeEnd <= rangeStart) {
      throw new Error('Invalid coverage time range');
    }

    const key = cacheKey({
      aoiHash: hashAoi(targetGeoJson),
      startMs: rangeStart,
      endMs: rangeEnd,
      noradIds,
      includeGeometry,
    });
    const cached = options.skipCache ? null : await readCache(key);
    if (cached) {
      const cachedResult: AoiCoverageResult = {
        geojson: cached.document,
        queriedAt: new Date().toISOString(),
        summary: summarizeCoverage(cached.document),
      };
      if (!quiet) {
        coverageResult.value = cachedResult;
        status.value = 'success';
      }
      // Stale-while-revalidate: the user already has a painted result, so refresh
      // in the background and let the next open pick up the newer data.
      if (cached.stale) {
        void submitCoverage({
          ...options,
          quiet: true,
          skipCache: true,
          range: [rangeStart, rangeEnd],
        });
      }
      return cachedResult;
    }

    const params = new URLSearchParams({
      start: toApiIso(rangeStart),
      end: toApiIso(rangeEnd),
      norad_ids: noradIds.join(','),
    });
    if (!includeGeometry) params.set('include_geometry', 'false');
    const response = await authFetch(`/api/coverage?${params.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body,
      signal: controller.signal,
    });
    if (!response.ok) {
      let detail = '';
      try {
        const errorJson = await response.json();
        detail = (errorJson as Record<string, string>).error
          || (errorJson as Record<string, string>).detail
          || '';
      } catch {
        detail = await response.text().catch(() => '');
      }
      throw new Error(detail || `Server returned ${response.status}`);
    }
    const document = await response.json() as Record<string, unknown>;
    const resultJson = normalizeCoverageDocument(document);

    const result: AoiCoverageResult = {
      geojson: resultJson as Record<string, unknown>,
      queriedAt: new Date().toISOString(),
      summary: summarizeCoverage(resultJson),
    };
    void writeCache(key, resultJson as Record<string, unknown>);
    if (!quiet) {
      coverageResult.value = result;
      status.value = 'success';
    }
    return result;
  } catch (err) {
    if (quiet) return null;   // a background warm must never surface an error banner
    if ((err as Error).name === 'AbortError') {
      errorMessage.value = 'Request timed out (2 min)';
    } else {
      errorMessage.value = (err as Error).message || 'Network error';
    }
    status.value = 'error';
    return null;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    if (abortFromCaller) {
      options.signal?.removeEventListener('abort', abortFromCaller);
    }
  }
}

function setAoiGeoJson(name: string, geojson: Record<string, unknown>): boolean {
  const normalized = normalizeGeoJson(geojson);
  const normalizedGeoJson = normalized.geojson;
  if (normalized.error || !normalizedGeoJson) {
    aoiFile.value = null;
    aoiGeoJson.value = null;
    coverageResult.value = null;
    status.value = 'error';
    errorMessage.value = normalized.error ?? 'Invalid GeoJSON';
    noticeMessage.value = null;
    return false;
  }

  aoiFile.value = new File([JSON.stringify(normalizedGeoJson)], `${name}.geojson`, { type: 'application/geo+json' });
  aoiGeoJson.value = normalizedGeoJson;
  coverageResult.value = null;
  status.value = 'idle';
  errorMessage.value = null;
  noticeMessage.value = normalized.notes.length > 0 ? normalized.notes.join(' ') : null;
  return true;
}

function summarizeCoverage(data: unknown): string {
  if (!data || typeof data !== 'object') return 'Coverage result received';
  const doc = data as Record<string, unknown>;
  if (doc.type === 'FeatureCollection' && Array.isArray(doc.features)) {
    return `${doc.features.length} coverage feature(s) returned`;
  }
  if (Array.isArray(data)) {
    return `${(data as unknown[]).length} coverage result(s) returned`;
  }
  return 'Coverage result received';
}

function clearAoi(): void {
  aoiFile.value = null;
  aoiGeoJson.value = null;
  coverageResult.value = null;
  status.value = 'idle';
  errorMessage.value = null;
  noticeMessage.value = null;
}

function clearResult(): void {
  coverageResult.value = null;
  status.value = aoiGeoJson.value ? 'idle' : 'idle';
  errorMessage.value = null;
}

/* ──────────────────────── Composable ─────────────────────── */

export function useAoiStore() {
  return {
    aoiFile: readonly(aoiFile),
    aoiGeoJson: readonly(aoiGeoJson),
    coverageResult: readonly(coverageResult),
    status: readonly(status),
    errorMessage: readonly(errorMessage),
    noticeMessage: readonly(noticeMessage),

    loadAoiFile,
    setAoiGeoJson,
    submitCoverage,
    clearAoi,
    clearResult,
  };
}
